// External libraries/modules
import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import parser from 'bbcode-to-react';
import { batch, useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

// Absolute imports
import useTranslations from '@hooks/useTranslations';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { usePrices } from '@hooks/usePriceCalculator';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import { useFinanceWidgetDuck } from '@hooks/useFinanceWidgetDuck';
import { BasketSummaryDetailsProps } from '@components/BasketSummaryDetails';
import PriceSuffix from '@components/PriceSuffix';
import Icon, { Icons } from '@components/Icon';
import PromoCode from '@components/PromoCodeInput';
import PromoCodeResult from '@components/PromoCodeResult';
import Spinner from '@components/Spinner';
import IconButton from '@components/IconButton';
import MonthlyPriceLabel, { AprSizes } from '@components/MonthlyPriceLabel';
import { PriceVatExcluded } from '@components/PriceVatExcluded';
import { useIsShowPriceExcludedVAT } from '@hooks/useIsShowPriceExcludedVAT';
import { usePriceBoxDetails } from '@hooks/usePriceDetails';
import { useSummaryDataPromise } from '@components/OfferListItem/hooks/useSummaryDataPromise';
import { pricesTemplateId, pxTemplateId } from '@components/FAQs';
import { Redux } from 'src/redux/redux.interface';
import { IDealerFee } from '@components/DealerFees/DealerFeesTemplate';
import { Link } from '@components/v2/link';
import {
    CARTE_GRISE_FEE,
    FINANCE_DETAIL_VIEW,
    isBrandAC,
    isBrandAP,
    isBrandDS,
    isBrandOV,
    isMarketFR,
    isMarketGB,
    isMarketIT,
    MALUS_FEE,
    REGISTRATION_FEE,
    REGISTRATION_FEES_VALUES,
    ROLES,
    ModalVersion,
    LEASYS_PROVIDER_TYPE,
    PxVersion,
} from 'src/constants/main';
import { getEcologicalBonus, getFormattedOfferValidityDate, openC2SWidget } from '@utils/Car.utils';
import {
    getFinanceDetailText,
    getFinanceDetailView,
    getMonthlyPriceObjByType,
    getStorePriceByPaymentMethod,
    renderPromotionalText,
} from '@utils/Price.utils';
import { getAgentDiscount, getPromoCodeDiscount } from '@utils/Deal.utils';
import { checkDealRentingProductInAllowedRentingProducts } from '@utils/rentingProducts.utils';
import { getSessionIdCookie } from '@utils/Session.utils';
import DealService from 'src/services/deal/deal.service';
import { StockFormatedPriceType } from 'src/services/stock/models/stockItem.price.model';
import { ICalculateSummaryRequestData } from 'src/services/calculateSummary/calculateSummary.types';
import { IAccessory } from 'src/services/carDetails/carDetails.types';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import CarDetailsDuck from 'src/redux/carDetails/carDetails.duck';
import ConfiguratorDuck from 'src/redux/configurator/configurator.duck';
import { useJourneyType } from '@hooks/useJourneyType';
import { useIgnorePXBonus } from 'src/partExchange/hooks';
import { useRouter } from 'next/router';
import { getModelTitle } from 'src/services/carList/utils/Car.utils';
import routes from '../../constants/routes';
import { theme } from '../../styles/theme';
import { FEATURES_LIST, FeatureSwitchContext } from '../../context/featureSwitchApp';
import { Pages } from '../../types/pages';
import { PaymentJourneyTypes } from '../../partExchange/interfaces/Default';
import { IconTemplate } from '@components/Icon/IconTemplate';
import { ContinueCTAButton } from '@components/ContinueCTAButton';
import { formatDateByLang } from '@utils/Date.utils';
import { usePersonalizedOffer } from '@hooks/usePersonalizedOffer';
import Tooltip, { Effect } from '@components/GlobalTooltip';
import FinanceWidgetDuck from 'src/redux/financeWidget/financeWidget.duck';
import { useGTM } from '@hooks/useGTM';
import Button from '@components/Button';
import {
    PX_STATUSES_APPROVED,
    PX_STATUSES_ERROR,
    PX_STATUSES_IN_PROGRESS,
    PX_STATUSES_WAITING,
} from '../../partExchange/constants/config';
import { getFeatureSwitch } from '@utils/featureSwitches.utils';
import { deletePartExchange } from '../../partExchange/service/ApiService';

const BasketSummaryDetailsTemplate: FC<BasketSummaryDetailsProps> = ({
    className,
    closePriceSummary,
    setIsOpenPriceDetails,
    isUserLoggedInAsDealer,
    page,
}) => {
    const { t, hasValue } = useTranslations();
    const { formatMoney } = useMoneyFormatter();
    const dispatch = useDispatch();
    const { pushToDataLayer } = useGTM();
    const context = useContext(FeatureSwitchContext);
    const router = useRouter();
    const { carDetails, currentDeal, promoCodeInDeal } = useCarDetailsDuck();
    const { financeQuote, pricesList, globalTexts, leasysQuote } = useFinanceWidgetDuck();
    const { isPersonalizedDeal } = usePersonalizedOffer();
    const autobizPxState = useSelector((state: Redux) => ConfiguratorDuck.getOwnState(state).pxState);
    const customPxState = currentDeal?.extraFields?.px ? JSON.parse(currentDeal?.extraFields?.px) : {};
    const { shouldIgnorePXBonus } = useIgnorePXBonus();
    const isShowPriceExcludedVAT: boolean = useIsShowPriceExcludedVAT();
    const isLoading = useSelector((state) => !!UIDuck.getOwnState(state).loading);

    const [showPromoCodeInput, setShowPromoCodeInput] = useState(true);
    const [isVisibleEcoBonusDetail, setIsVisibleEcoBonusDetail] = useState(false);

    const dealerFees = useMemo(
        () => (!isEmpty(currentDeal?.extraFields?.dealerFees) ? JSON.parse(currentDeal?.extraFields?.dealerFees) : []),
        [currentDeal?.extraFields?.dealerFees]
    );

    const EXCLUDED_FEES = [CARTE_GRISE_FEE, REGISTRATION_FEE, MALUS_FEE];

    const findDealerFee = (
        dealerFees: Array<{ id: string | number; postalCode: string; priceInclTax: string; title: string }>,
        feeId: typeof CARTE_GRISE_FEE | typeof REGISTRATION_FEE | typeof MALUS_FEE
    ) => dealerFees?.find((dealerFee) => dealerFee.id === feeId);

    const pxVersion = getFeatureSwitch(FEATURES_LIST.FEATURE_SWITCH_PX_VERSION, context);
    const isDealerFeesFsEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_DEALER_FEES_ENABLED);
    const isDealerFeesEnabled = !router.pathname.includes(routes.DEALER) && isDealerFeesFsEnabled;
    const { paymentJourneyType, isRent } = useJourneyType();
    const { hasSelectedPaymentJourney = false } = currentDeal?.extraFields ?? {};
    const hasSelectedFinance = hasSelectedPaymentJourney && paymentJourneyType === PaymentJourneyTypes.FINANCE;
    const hasSelectedCash = hasSelectedPaymentJourney && paymentJourneyType === PaymentJourneyTypes.CASH;
    const hasSelectedLeasys =
        hasSelectedPaymentJourney &&
        paymentJourneyType === PaymentJourneyTypes.FINANCE &&
        currentDeal?.extraFields?.providerType === LEASYS_PROVIDER_TYPE;
    const carteGriseFee = findDealerFee(carDetails?.fees, CARTE_GRISE_FEE);
    const registrationFee = findDealerFee(dealerFees, REGISTRATION_FEE);
    const malusDealerFee = findDealerFee(dealerFees, MALUS_FEE);

    const filteredMandatoryDealerFees = dealerFees.filter(
        (dealerFee: IDealerFee) => dealerFee.mandatory && !EXCLUDED_FEES.includes(dealerFee.id)
    );
    const filteredOptionalDealerFees = carDetails?.fees?.filter(
        (dealerFee: IDealerFee) => !dealerFee.mandatory && !EXCLUDED_FEES.includes(dealerFee.id)
    );
    const selectedOptionalAccessories = carDetails?.accessories?.filter((accessory: IAccessory) => accessory.selected);

    const { cash, finance, catalog } = usePrices(
        carDetails ?? null,
        false,
        null,
        true,
        !isDealerFeesEnabled,
        paymentJourneyType,
        paymentJourneyType === PaymentJourneyTypes.CASH
    );
    const {
        totalPrice: totalCashPrice,
        totalPriceWithoutPaymentJourney: { cash: totalCashPriceWithoutSelectedPaymentJourney },
    } = cash;
    const {
        totalPrice: totalMonthlyPrice,
        financeProperties: { hasFinanceVariants, deposit, apr },
    } = finance;

    const params: ICalculateSummaryRequestData = {
        carConfigId: carDetails._id ?? carDetails.externalId,
        token: financeQuote?.token ?? currentDeal?.financeQuoteId,
        carConfiguration: carDetails,
    };
    const { totalPrice: baseCatalogPrice } = catalog;
    const { totalPrice: baseTotalPrice, totalBasicPriceWithoutVat } = cash;

    const { processCalculateSummaryDataPromise } = useSummaryDataPromise(
        {
            ...params,
            ...(hasSelectedLeasys &&
                currentDeal?.extraFields?.quotationId && { quotationId: currentDeal?.extraFields?.quotationId }),
        },
        hasSelectedLeasys ? LEASYS_PROVIDER_TYPE : null
    );

    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId);

        if (sectionId === 'price') {
            setIsOpenPriceDetails(true);
        }

        return element.scrollIntoView();
    };

    const isOfferValidityDateEnabled = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_OFFER_VALIDITY_DATE_ENABLED
    );
    const isFinanceEnabled = !useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_IS_FINANCE_DISABLED);

    const showOfferValidityDate: boolean = isOfferValidityDateEnabled && isFinanceEnabled && hasFinanceVariants;

    const isPromoCodeEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_PROMO_CODES_ENABLED);
    const showEcoBonus = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SHOW_ECO_BONUS);

    const offerValidityDateBasicOffer = getFormattedOfferValidityDate(carDetails?.importInputs);
    const offerValidityDatePersonalizeOffer = formatDateByLang(currentDeal?.endDate);

    const monthlyPriceObj = getMonthlyPriceObjByType(carDetails.prices, StockFormatedPriceType.EMPLOYEE);
    const amountRounding = monthlyPriceObj?.amountRounding;

    const promoText =
        financeQuote?.primaryText ?? (carDetails?.extraFields?.promotionalText || carDetails?.promotionalText);

    const financeDetailView = getFinanceDetailView(carDetails.prices, StockFormatedPriceType.EMPLOYEE);
    const isSOLEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SOL_ENABLED);

    const selectedFeesChoice = currentDeal?.extraFields?.selectedFeesValue;

    const storePriceByPaymentMethod = getStorePriceByPaymentMethod(
        currentDeal,
        hasSelectedPaymentJourney ? paymentJourneyType : null,
        isSOLEnabled
    );
    const { hasEcologicalBonus, ecologicalBonusValue, ecologicalBonusTitleKey } = getEcologicalBonus(carDetails);

    const displayEcoBonusDescription = Boolean(!(isBrandOV && (isMarketIT || isMarketFR)));
    const financeDetailText = getFinanceDetailText(
        carDetails.prices,
        StockFormatedPriceType.EMPLOYEE,
        globalTexts,
        pricesList,
        carDetails.externalId || carDetails.vehicleId
    );

    const { isCataloguePriceFeatureSwitchEnabled } = usePriceBoxDetails({
        catalogPrice: baseCatalogPrice,
        basePrice: baseTotalPrice,
        financeDetailView,
        monthlyPrice: totalMonthlyPrice,
    });

    useEffect(() => {
        const pricesTemplateElement = document.getElementById(pricesTemplateId);

        const handleScroll = () => {
            if (pricesTemplateElement) {
                pricesTemplateElement.style.maxHeight =
                    window.pageYOffset <= 100 ? 'calc(100vh - 300px)' : 'calc(100vh - 145px)';
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        const updateDeal = async () => {
            if (filteredOptionalDealerFees?.length > 1) {
                for (const selectedDealerFee of filteredOptionalDealerFees) {
                    const notEnabledFees = selectedDealerFee?.notCompatible?.split(',') ?? [];

                    const otherFilteredOptionalDealerFees = filteredOptionalDealerFees.filter(
                        (dealerFee: IDealerFee) => dealerFee.id !== selectedDealerFee.id
                    );

                    const feeToUpdate = otherFilteredOptionalDealerFees.find((otherFee: IDealerFee) =>
                        notEnabledFees.includes(otherFee.id)
                    );

                    if (feeToUpdate) {
                        dispatch(UIDuck.setLoading(5));

                        const selectedFeesChoise = currentDeal?.extraFields?.selectedFeesValue;
                        const { data } = await DealService.updateDeal(
                            {
                                dealerFees: carDetails?.fees?.filter(
                                    (dealerFee: IDealerFee) =>
                                        dealerFee.mandatory || EXCLUDED_FEES.includes(dealerFee.id)
                                ),
                                ...(currentDeal?.extraFields?.postalCode && {
                                    postalCode: currentDeal?.extraFields?.postalCode,
                                }),
                                includeFees: selectedFeesChoise === REGISTRATION_FEES_VALUES.YES,
                            },
                            getSessionIdCookie(),
                            true
                        );

                        batch(() => {
                            dispatch(CarDetailsDuck.setCurrentDeal(null));
                            dispatch(CarDetailsDuck.setCurrentDeal(data?.deal));
                            dispatch(CarDetailsDuck.setCurrentCarDetails(data?.deal?.fullProductConfiguration));

                            if (data?.financeQuote) {
                                dispatch(FinanceWidgetDuck.setFinanceQuote(data?.financeQuote));
                            }
                            dispatch(UIDuck.setLoading(-5));
                        });

                        break;
                    }
                }
            }
        };

        updateDeal().catch((error) => console.error(error));
    }, []);

    useEffect(() => {
        const CONTINUE_BUTTON_HEIGHT = 90;
        const WITH_BORDER_CLASS_NAME = 'withBorderTop';

        const wrapper = document.getElementById('summary');
        const continueBtn = document.getElementById('continue');

        if (continueBtn) {
            window.addEventListener('scroll', () => {
                if (continueBtn.offsetTop + CONTINUE_BUTTON_HEIGHT < wrapper.clientHeight) {
                    continueBtn.classList.add(WITH_BORDER_CLASS_NAME);
                } else {
                    continueBtn.classList.remove(WITH_BORDER_CLASS_NAME);
                }
            });
        }
    }, []);

    const isBasket = router.pathname.includes(routes.BASKET);

    const rentingProductsFs = useContext(FeatureSwitchContext)?.FEATURE_SWITCH_RENTING_PRODUCTS ?? '';
    const isDemoVehicle = carDetails?.mileage && carDetails?.registrationDate;
    const hasAllowedRentingProduct =
        hasSelectedFinance &&
        checkDealRentingProductInAllowedRentingProducts(rentingProductsFs, financeQuote?.financialType);
    const showPxC2SButton =
        pxVersion === PxVersion.v2 &&
        autobizPxState?.externalReferenceId &&
        PX_STATUSES_IN_PROGRESS.includes(autobizPxState?.statusId);
    const showPxWaitingStatus =
        pxVersion === PxVersion.v2 && autobizPxState ? PX_STATUSES_WAITING.includes(autobizPxState.statusId) : false;
    const showPxApprovedStatus =
        pxVersion === PxVersion.v2 && autobizPxState ? PX_STATUSES_APPROVED.includes(autobizPxState.statusId) : false;
    const showPxErrorStatus =
        pxVersion === PxVersion.v2 && autobizPxState ? PX_STATUSES_ERROR.includes(autobizPxState.statusId) : false;

    return (
        <div className={className} id="summary">
            <div className="title">
                <span className="label">
                    {page === Pages.dealer
                        ? getModelTitle(carDetails, carDetails?.displayGrBodyStyleLabel)
                        : t('basket.summary.title')}
                    {isUserLoggedInAsDealer && isBasket && (
                        <IconTemplate
                            onClick={() => {
                                pushToDataLayer({
                                    event: 'uaevent',
                                    eventCategory: 'Content',
                                    eventAction: 'Display::EditConfigurationModal',
                                    eventLabel: 'Configuration modal',
                                });
                                dispatch(
                                    UIDuck.openModal({
                                        data: {},
                                        modalType: UIDuck.MODAL_TYPES.PERSONALIZE_OFFER,
                                        modalVersion: ModalVersion.v2,
                                        modalProperties: {
                                            withoutPadding: true,
                                        },
                                    })
                                );
                            }}
                            name={Icons.edit}
                            width={26}
                            height={26}
                        />
                    )}
                </span>
                {closePriceSummary && (
                    <span onClick={closePriceSummary} className="closeIcon">
                        <Icon width={12} height={12} name={Icons.Cancel} />
                    </span>
                )}
            </div>
            <div className="prices" id="prices">
                {isPersonalizedDeal && offerValidityDatePersonalizeOffer && (
                    <div className="offerValidityDate">
                        {t('basket.offerValidityDate')} {offerValidityDatePersonalizeOffer}
                        <div className="tooltip">
                            <button
                                className="inputToolTip"
                                data-for="inputToolTip"
                                data-html="true"
                                data-tip={`<div style="text-align: left">${t(
                                    'basket.offerValidityDate.tooltip'
                                )}</div>`}
                            >
                                <Icon className="infoIcon" name={Icons.InfoCircle} width={14} height={14} />
                            </button>
                        </div>
                        <Tooltip
                            id="inputToolTip"
                            effect={Effect.solid}
                            scrollHide={false}
                            event="click"
                            eventOff="mouseout blur"
                            delayHide={300}
                        />
                    </div>
                )}
                {isCataloguePriceFeatureSwitchEnabled && !isDemoVehicle && (
                    <div className="priceSection">
                        <div className="priceSection_title">{t('basket.summary.vehicle')}</div>
                        <div className="priceSection_value">
                            <span>{t('price.catalog.title')}</span>
                            <span>
                                {formatMoney(
                                    catalog?.totalPrice !== undefined ? catalog.totalPrice : totalCashPrice,
                                    true
                                )}
                            </span>
                        </div>
                    </div>
                )}
                {!hasSelectedLeasys && storePriceByPaymentMethod && (
                    <div className="storePrice">
                        {!hasAllowedRentingProduct && (
                            <div className="storePrice_value">
                                <span className="storePrice_title">{t('basket.summary.storePrice')}</span>
                                {isLoading ? (
                                    <Spinner size={20} border={3} color={theme.colors.black} />
                                ) : (
                                    <span className="storePrice_value_price" data-testid="TESTING_STORE_PRICE">
                                        {formatMoney(
                                            (getPromoCodeDiscount(currentDeal, paymentJourneyType)
                                                ? storePriceByPaymentMethod +
                                                  getPromoCodeDiscount(currentDeal, paymentJourneyType)
                                                : storePriceByPaymentMethod) + getAgentDiscount(currentDeal),
                                            false
                                        )}
                                        <PriceSuffix checkPrioritizedTranslation showOnlyPrioritizedTranslation />
                                    </span>
                                )}
                            </div>
                        )}
                        {!isDemoVehicle && (
                            <Link
                                isUnderline
                                style={{ display: 'flex', justifyContent: 'flex-end' }}
                                txt={t('basket.summary.vehiclePrice')}
                                onClick={() => scrollToSection('price')}
                            >
                                <button onClick={() => scrollToSection('price')} className="storePrice_link">
                                    {t('basket.summary.vehiclePrice')}
                                </button>
                            </Link>
                        )}
                    </div>
                )}
                {promoCodeInDeal && (
                    <div className="promoCodeResult">
                        <PromoCodeResult
                            promoCode={promoCodeInDeal}
                            hideValue={hasAllowedRentingProduct}
                            isBasketSummary
                            showTitle={false}
                        />
                    </div>
                )}
                {!hasSelectedLeasys &&
                    currentDeal?.extraFields?.agent_discount &&
                    Number(JSON.parse(currentDeal?.extraFields?.agent_discount)?.amount) > 0 && (
                        <div className="dealerDiscountResult">
                            <span className="dealerDiscountResult_title">{t('basket.summary.dealerDiscount')}</span>
                            <span className="dealerDiscountResult_value">
                                -{' '}
                                {formatMoney(
                                    Number(JSON.parse(currentDeal?.extraFields?.agent_discount)?.amount),
                                    false
                                )}
                            </span>
                        </div>
                    )}
                {isDealerFeesEnabled && selectedFeesChoice === REGISTRATION_FEES_VALUES.YES && (
                    <>
                        <div className="priceSection">
                            <div className="priceSection_title">{t('basket.orderSummary.registrationFees.title')}</div>
                            {carteGriseFee?.postalCode && (
                                <div className="priceSection_value">
                                    <span className="priceSection_title">{t('basket.fees.carteGrise.title')}</span>
                                    <span className="value" data-testid="TESTING_SUMMARY_CARTE_GRISE_PRICE">
                                        {formatMoney(carteGriseFee?.priceInclTax)}
                                    </span>
                                </div>
                            )}
                            {registrationFee && (
                                <div className="priceSection_value">
                                    <span className="priceSection_title">{t(registrationFee.title)}</span>
                                    <span className="value" data-testid="TESTING_SUMMARY_REGISTRATION_FEE_PRICE">
                                        {formatMoney(registrationFee?.priceInclTax, true)}
                                    </span>
                                </div>
                            )}
                            {malusDealerFee && (
                                <div className="priceSection_value">
                                    <span className="priceSection_title">{t(malusDealerFee.title)}</span>
                                    <span className="value" data-testid="TESTING_SUMMARY_MALUS_FEE_PRICE">
                                        {formatMoney(malusDealerFee.priceInclTax, true)}
                                    </span>
                                </div>
                            )}
                        </div>
                    </>
                )}
                {isDealerFeesEnabled && filteredMandatoryDealerFees?.length > 0 && !isRent && (
                    <div data-testid="mandatoryFees" id="mandatoryFees" className="priceSection withoutPadding">
                        <div className="priceSection_title">{t('basket.orderSummary.mandatoryDealerFees.title')}</div>
                        {filteredMandatoryDealerFees.map((dealerFee: IDealerFee, key: number) => (
                            <div key={key} className="priceSection_value">
                                <span className="priceSection_title">{dealerFee.title}</span>
                                <span
                                    className="value"
                                    data-testid={`TESTING_SUMMARY_MANDATORY_FEE_${dealerFee.title?.toUpperCase()}_PRICE`}
                                >
                                    {formatMoney(dealerFee.priceInclTax, true)}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
                {isDealerFeesEnabled && filteredOptionalDealerFees?.length > 0 && !isRent && (
                    <div data-testid="optionalFees" id="optionalFees" className="priceSection withoutPadding">
                        <div className="priceSection_title">{t('basket.orderSummary.optionalDealerFees.title')}</div>
                        {filteredOptionalDealerFees.map((dealerFee: IDealerFee, key: number) => (
                            <div key={key} className="priceSection_value">
                                <span className="priceSection_title">{dealerFee.title}</span>
                                <span
                                    className="value"
                                    data-testid={`TESTING_SUMMARY_OPTIONAL_FEE_${dealerFee?.title?.toUpperCase()}_PRICE`}
                                >
                                    {formatMoney(dealerFee.priceInclTax, false)}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
                {selectedOptionalAccessories?.length > 0 && (
                    <div data-testid="accessories" className="priceSection withoutPadding">
                        <div className="priceSection_title">{t('basket.orderSummary.optionalAccessories.title')}</div>
                        {selectedOptionalAccessories.map((accessory: IAccessory, key: number) => (
                            <div key={key} className="priceSection_value">
                                <span className="priceSection_title">{parser.toReact(accessory.label)}</span>
                                <span
                                    className="value"
                                    data-testid={`TESTING_SUMMARY_ACCESSORY_${accessory?.label?.toUpperCase()}_PRICE`}
                                >
                                    {formatMoney(accessory.netAmount, false)}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
                <div className="totalPrice">
                    {isLoading ? (
                        <Spinner className="totalPriceLoading" size={20} border={3} color={theme.colors.black} />
                    ) : (
                        <>
                            {isDealerFeesEnabled && hasAllowedRentingProduct && (
                                <>
                                    <span className="totalPrice_title">{t('basket.orderSummary.feesTotal')}</span>
                                    <div className="totalPrice_cashPrice">
                                        {formatMoney(
                                            carDetails?.fees?.reduce(
                                                (sum: number, fee: IDealerFee) =>
                                                    sum +
                                                    fee.priceInclTax +
                                                    (carDetails?.importInputs?.accessoriesPrice || 0),
                                                0
                                            ),
                                            false
                                        )}
                                    </div>
                                </>
                            )}
                            {hasSelectedLeasys && (
                                <div className="totalPrice_section">
                                    <span className="totalPrice_title noWrap">{'Leasys (TTC)'}</span>
                                    <div
                                        className={`${
                                            isShowPriceExcludedVAT ? 'isShowPriceExcludedVAT-container' : ''
                                        }`}
                                    >
                                        <div
                                            data-testid="TESTING_TOTAL_LEASYS_PRICE"
                                            className={`totalPrice_cashPrice ${hasSelectedCash ? 'isCash' : ''}`}
                                        >
                                            {formatMoney(leasysQuote?.monthlyPayment, false, '', amountRounding)}
                                            <PriceSuffix isMonthlyPrice />
                                        </div>
                                        <div className="totalPrice_label">{leasysQuote?.primaryText?.[0]?.text}</div>
                                        <MonthlyPriceLabel
                                            withPrimaryColor={isMarketGB}
                                            alignToRight
                                            isSummary
                                            aprValue={apr}
                                            aprCustomTextKey={promoText}
                                            size={AprSizes.MEDIUM}
                                            calculateSummaryData={processCalculateSummaryDataPromise}
                                            financeDetailText={t('summary.financeDetail')}
                                        />
                                    </div>
                                </div>
                            )}

                            {hasSelectedCash &&
                                pxVersion === PxVersion.v2 &&
                                showPxApprovedStatus &&
                                (autobizPxState?.id || customPxState?.vehicle?.isActive) && (
                                    <div className="pxSection withoutBackground">
                                        <div className="pxSection_title">
                                            <span>
                                                {t(`basket.summary.px.title${customPxState?.vehicle ? '.custom' : ''}`)}
                                            </span>
                                            <Link
                                                className="link"
                                                isUnderline
                                                txt={t('basket.summary.px.detail')}
                                                onClick={() => scrollToSection(pxTemplateId)}
                                                ignoreParent
                                            >
                                                <button className="link" onClick={() => scrollToSection(pxTemplateId)}>
                                                    {t('basket.summary.px.detail')}
                                                    <IconTemplate
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            dispatch(
                                                                UIDuck.openModal({
                                                                    data: {
                                                                        onConfirmClick: () =>
                                                                            deletePartExchange(
                                                                                null,
                                                                                null,
                                                                                currentDeal?.partExchangeId
                                                                            ),
                                                                    },
                                                                    modalType:
                                                                        UIDuck.MODAL_TYPES.REMOVE_PX_CONFIRMATION,
                                                                    modalVersion: ModalVersion.v2,
                                                                    modalProperties: {
                                                                        centerModeOnDesktop: true,
                                                                    },
                                                                })
                                                            );
                                                        }}
                                                        name={Icons.Trash}
                                                    />
                                                </button>
                                            </Link>
                                        </div>
                                        <div className="pxSection_value">
                                            <span>
                                                {t(
                                                    `basket.summary.px.trade${customPxState?.vehicle ? '.custom' : ''}${
                                                        showPxApprovedStatus ? '.approved' : ''
                                                    }`
                                                )}
                                            </span>
                                            <span className="link">
                                                {formatMoney(
                                                    autobizPxState?.price || customPxState?.prices?.dealerValuation,
                                                    true
                                                )}
                                            </span>
                                        </div>
                                        {!shouldIgnorePXBonus &&
                                            (autobizPxState?.bonusAmount > 0 ||
                                                customPxState?.prices?.dealerBonusAmount > 0) && (
                                                <div className="pxSection_value">
                                                    <span>
                                                        {t(
                                                            `basket.summary.px.bonus${
                                                                customPxState?.vehicle ? '.custom' : ''
                                                            }`
                                                        )}
                                                    </span>
                                                    <span className="link">
                                                        {formatMoney(
                                                            autobizPxState?.bonusAmount ||
                                                                customPxState?.prices?.dealerBonusAmount,
                                                            true
                                                        )}
                                                    </span>
                                                </div>
                                            )}
                                    </div>
                                )}

                            {!hasSelectedLeasys && !hasAllowedRentingProduct && (
                                <div className="totalPrice_section">
                                    <span className="totalPrice_title">{t('basket.orderSummary.orderTotal')}</span>
                                    <div
                                        className={`${
                                            isShowPriceExcludedVAT ? 'isShowPriceExcludedVAT-container' : ''
                                        }`}
                                    >
                                        <div
                                            data-testid="TESTING_TOTAL_CASH_PRICE"
                                            className={`totalPrice_cashPrice ${hasSelectedCash ? 'isCash' : ''}`}
                                        >
                                            {formatMoney(
                                                !hasSelectedPaymentJourney
                                                    ? totalCashPriceWithoutSelectedPaymentJourney +
                                                          (carDetails?.importInputs?.accessoriesPrice || 0)
                                                    : totalCashPrice +
                                                          (carDetails?.importInputs?.accessoriesPrice || 0),
                                                false
                                            )}
                                            <PriceSuffix showOnlyPrioritizedTranslation checkPrioritizedTranslation />
                                        </div>
                                        {isShowPriceExcludedVAT && (
                                            <div
                                                className={`totalPrice_cashPrice isShowPriceExcludedVAT ${
                                                    hasSelectedCash ? 'isCash' : ''
                                                }`}
                                            >
                                                <PriceVatExcluded
                                                    price={formatMoney(totalBasicPriceWithoutVat, false)}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {hasSelectedCash && (
                                <div className="totalPrice_cashPrice_label">
                                    {t('basket.orderSummary.cash.description1')}
                                </div>
                            )}
                            {hasSelectedCash && (
                                <div className="totalPrice_cashPrice_label">
                                    {t('basket.orderSummary.cash.description2')}
                                </div>
                            )}
                            {!isShowPriceExcludedVAT &&
                                (!hasSelectedPaymentJourney || hasSelectedFinance) &&
                                totalMonthlyPrice &&
                                !hasSelectedLeasys && (
                                    <>
                                        <div className="totalPrice_monthlyPrice">
                                            <span>
                                                {hasSelectedFinance && (
                                                    <span className="totalPrice_monthlyPrice_title">
                                                        {t('basket.summary.monthlyPrice')}
                                                    </span>
                                                )}
                                            </span>
                                            <span
                                                data-testid="TESTING_TOTAL_MONTHLY_PRICE"
                                                className="totalPrice_monthlyPrice_price"
                                            >
                                                {!hasSelectedPaymentJourney && (
                                                    <span className="prefix">{t('or')}</span>
                                                )}
                                                <span className="formatted">
                                                    {formatMoney(totalMonthlyPrice, false, '', amountRounding)}
                                                </span>
                                                <span className="suffix">
                                                    <PriceSuffix checkPrioritizedTranslation isMonthlyPrice />
                                                </span>
                                                {financeDetailView === FINANCE_DETAIL_VIEW.ICON && (
                                                    <IconButton
                                                        className="infoIcon"
                                                        icon={
                                                            isBrandAC || isBrandAP || isBrandDS
                                                                ? Icons.InfoCircle
                                                                : Icons.Info
                                                        }
                                                        width={16}
                                                        height={16}
                                                        onClick={processCalculateSummaryDataPromise}
                                                    />
                                                )}
                                            </span>
                                        </div>
                                        <div className="totalPrice_label">
                                            {!isMarketGB &&
                                                renderPromotionalText(
                                                    promoText,
                                                    t('price.pattern'),
                                                    deposit,
                                                    null,
                                                    null,
                                                    null,
                                                    null,
                                                    { apr }
                                                )}
                                        </div>
                                        {financeDetailView === FINANCE_DETAIL_VIEW.LINK && (
                                            <MonthlyPriceLabel
                                                withPrimaryColor={isMarketGB}
                                                alignToRight
                                                isSummary
                                                aprValue={apr}
                                                aprCustomTextKey={promoText}
                                                size={AprSizes.MEDIUM}
                                                calculateSummaryData={processCalculateSummaryDataPromise}
                                                financeDetailText={financeDetailText}
                                            />
                                        )}
                                    </>
                                )}
                        </>
                    )}
                    {!isPersonalizedDeal && showOfferValidityDate && offerValidityDateBasicOffer && (
                        <div className="totalPrice_leadTime">
                            {t('basket.offerValidityDate')} {offerValidityDateBasicOffer}
                        </div>
                    )}
                </div>
                {hasEcologicalBonus && showEcoBonus && (
                    <div
                        className={`ecoBonusWrapper ${
                            hasSelectedCash ? 'ecoBonusWrapper-cash' : 'ecoBonusWrapper-finance'
                        }`}
                    >
                        <span>
                            {t(ecologicalBonusTitleKey)}
                            {displayEcoBonusDescription && (
                                <button onClick={() => setIsVisibleEcoBonusDetail(!isVisibleEcoBonusDetail)}>
                                    <Icon
                                        name={Icons.InfoCircle}
                                        color={theme.colors.primary}
                                        height={16}
                                        width={16}
                                        className="ecoBonusWrapper_icon"
                                        role={ROLES.IMG}
                                        altId="svgImgEcoBonus"
                                        altText={t(ecologicalBonusTitleKey)}
                                    />
                                </button>
                            )}
                        </span>
                        <span className="ecoBonusWrapper_price">- {formatMoney(ecologicalBonusValue, true)}</span>
                    </div>
                )}

                {isVisibleEcoBonusDetail && displayEcoBonusDescription && (
                    <div
                        className={`ecoBonusWrapper_description ecoBonusWrapper_description--${
                            hasSelectedCash ? 'cash' : 'finance'
                        } ${
                            isVisibleEcoBonusDetail && displayEcoBonusDescription
                                ? 'ecoBonusWrapper_description--opened'
                                : ''
                        }`}
                    >
                        {t('basket.ecoBonus.description')}
                    </div>
                )}

                {!hasSelectedLeasys && isPromoCodeEnabled && isBasket && (
                    <div className={`promoCodeSection ${showPromoCodeInput ? 'isOpen' : 'isHide'}`}>
                        <button
                            onClick={() => setShowPromoCodeInput(!showPromoCodeInput)}
                            className={`promoCodeSection_title ${showPromoCodeInput ? 'isOpen' : 'isHide'}`}
                        >
                            {((isBrandOV && isMarketGB) || isBrandAC || isBrandDS) && (
                                <Icon width={12} height={11} name={isBrandDS ? Icons.ChevronDown : Icons.Plus} />
                            )}
                            <span className={`${showPromoCodeInput ? 'isOpen' : 'isHide'}`}>
                                {t('promoCode.title')}
                            </span>
                            {!isBrandOV || (!isMarketGB && <Icon width={12} height={11} name={Icons.Plus} />)}
                        </button>
                        {showPromoCodeInput && (
                            <div className="promoCodeSectionWrapper">
                                <PromoCode
                                    page={Pages.basketV2}
                                    validateSelectedPaymentMethod
                                    showTitle={false}
                                    noPadding
                                />
                            </div>
                        )}
                    </div>
                )}

                {(autobizPxState?.id || customPxState?.vehicle?.isActive) && (
                    <div className="pxSection">
                        {(!showPxApprovedStatus || (showPxApprovedStatus && hasSelectedFinance)) && (
                            <div className="pxSection_title">
                                <span>{t(`basket.summary.px.title${customPxState?.vehicle ? '.custom' : ''}`)}</span>
                                <Link
                                    className="link"
                                    isUnderline
                                    txt={t('basket.summary.px.detail')}
                                    onClick={() => scrollToSection(pxTemplateId)}
                                    ignoreParent
                                >
                                    <button className="link" onClick={() => scrollToSection(pxTemplateId)}>
                                        {t('basket.summary.px.detail')}
                                        {showPxApprovedStatus && hasSelectedFinance && (
                                            <IconTemplate
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    dispatch(
                                                        UIDuck.openModal({
                                                            data: {
                                                                onConfirmClick: () =>
                                                                    deletePartExchange(
                                                                        null,
                                                                        null,
                                                                        currentDeal?.partExchangeId
                                                                    ),
                                                            },
                                                            modalType: UIDuck.MODAL_TYPES.REMOVE_PX_CONFIRMATION,
                                                            modalVersion: ModalVersion.v2,
                                                            modalProperties: {
                                                                centerModeOnDesktop: true,
                                                            },
                                                        })
                                                    );
                                                }}
                                                name={Icons.Trash}
                                            />
                                        )}
                                    </button>
                                </Link>
                            </div>
                        )}
                        {(!showPxApprovedStatus || (showPxApprovedStatus && hasSelectedFinance)) && (
                            <div className="pxSection_value">
                                <span>
                                    {t(
                                        `basket.summary.px.trade${customPxState?.vehicle ? '.custom' : ''}${
                                            showPxApprovedStatus ? '.approved' : ''
                                        }`
                                    )}
                                </span>
                                <span className="link">
                                    {formatMoney(autobizPxState?.price || customPxState?.prices?.dealerValuation, true)}
                                </span>
                            </div>
                        )}
                        {(!showPxApprovedStatus || (showPxApprovedStatus && hasSelectedFinance)) &&
                            !shouldIgnorePXBonus &&
                            (autobizPxState?.bonusAmount > 0 || customPxState?.prices?.dealerBonusAmount > 0) && (
                                <div className="pxSection_value">
                                    <span>
                                        {t(`basket.summary.px.bonus${customPxState?.vehicle ? '.custom' : ''}`)}
                                    </span>
                                    <span className="link">
                                        {formatMoney(
                                            autobizPxState?.bonusAmount || customPxState?.prices?.dealerBonusAmount,
                                            true
                                        )}
                                    </span>
                                </div>
                            )}
                        {showPxC2SButton && (
                            <div className="pxSection_status">
                                <IconTemplate name={Icons.PxEdit} className="pxSection_status__icon" />
                                <p className="pxSection_status__label">{t('basket.px.trade.edit.label')}</p>
                                <p className="pxSection_status__description">{t('basket.px.trade.edit.description')}</p>
                                <Button
                                    secondary
                                    centerMode
                                    marginTop={25}
                                    withoutBackground={isBrandAC}
                                    onClick={() =>
                                        openC2SWidget(autobizPxState?.externalReferenceId, dispatch, currentDeal?.token)
                                    }
                                >
                                    {t('basket.px.trade.edit.button')}
                                </Button>
                            </div>
                        )}
                        {showPxErrorStatus && (
                            <div className="pxSection_status">
                                <IconTemplate name={Icons.PxError} className="pxSection_status__icon" />
                                <p className="pxSection_status__label">{t('basket.px.trade.error.label')}</p>
                            </div>
                        )}
                        {showPxWaitingStatus && (
                            <div className="pxSection_status">
                                <IconTemplate name={Icons.PxWaiting} className="pxSection_status__icon" />
                                <p className="pxSection_status__label">{t('basket.px.trade.waiting.label')}</p>
                                <p className="pxSection_status__description">
                                    {t('basket.px.trade.waiting.description')}
                                </p>
                            </div>
                        )}
                        {showPxApprovedStatus && (
                            <div className="pxSection_status">
                                <IconTemplate name={Icons.PxSuccess} className="pxSection_status__icon" />
                                <p className="pxSection_status__label">{t('basket.px.trade.success.label')}</p>
                                <p className="pxSection_status__description">
                                    {t('basket.px.trade.success.description')}
                                </p>
                            </div>
                        )}
                    </div>
                )}
                {currentDeal?.extraFields?.wallbox && (
                    <div className="wallboxSection">
                        <span className="wallboxSection_title">{t('basket.wallbox.summary.label')}</span>
                        <p className="wallboxSection_description">{t('basket.wallbox.summary.description')}</p>
                    </div>
                )}
                {hasValue('basket.payment.legal') && <p>{t('basket.payment.legal')}</p>}
            </div>
            {isUserLoggedInAsDealer && isBasket && (
                <div className="continueSection" id="continue">
                    <ContinueCTAButton page={Pages.basketV2} fullWidth />
                </div>
            )}
        </div>
    );
};

export default BasketSummaryDetailsTemplate;
